'use client';

import { useCallback, useEffect, useRef, useState, type FC } from 'react';
import { preload as preloadImage } from 'react-dom';
import { getImageProps } from 'next/image';
import { steamFallbackOnError, steamImageSrcSet } from '../../../lib/steamImage';

const STEAM_WIDTH = 512;
const STEAM_HEIGHT = 384;

// WHY: оптимизатор Next не растягивает картинку шире исходника, а у Steam он не шире 512 px — кандидаты шире первого
// ≥ 512 отдают тот же файл, а в HTML каждый стоит ~300 байт на картинку
const capSteamSrcSet = (srcSet: string | undefined): string | undefined => {
  const candidates = srcSet?.split(', ') ?? [];
  const last = candidates.findIndex((candidate) => Number(/ (\d+)w$/.exec(candidate)?.[1]) >= STEAM_WIDTH);
  return last === -1 ? srcSet : candidates.slice(0, last + 1).join(', ');
};

interface WikiSteamImageProps {
  src: string;
  alt: string;
  sizes: string;
  className?: string;
  loading: 'eager' | 'lazy';
  preload?: boolean;
  fetchPriority?: 'high' | 'low' | 'auto';
  fill?: boolean;
}

// WHY: основной путь — оптимизатор Next (WebP в разы легче PNG Steam); если он не отдал картинку (таймаут к Steam на
// холодном промахе), один раз переключаемся на тот же размер прямо с CDN Steam
export const WikiSteamImage: FC<WikiSteamImageProps> = ({
  src,
  alt,
  sizes,
  className,
  loading,
  preload = false,
  fetchPriority,
  fill = false,
}) => {
  const { props } = getImageProps({
    src,
    alt,
    sizes,
    className,
    preload,
    loading: preload ? undefined : loading,
    ...(fill ? { fill: true } : { width: STEAM_WIDTH, height: STEAM_HEIGHT }),
  });
  const srcSet = capSteamSrcSet(props.srcSet);
  if (preload) {
    preloadImage(props.src, { as: 'image', imageSrcSet: srcSet, imageSizes: props.sizes, fetchPriority });
  }

  const [fallenBackFor, setFallenBackFor] = useState<string | null>(null);
  const direct = fallenBackFor === src;
  const imgRef = useRef<HTMLImageElement>(null);

  const handleError = useCallback(() => {
    const next = steamFallbackOnError(src, fallenBackFor);
    if (next) setFallenBackFor(next);
  }, [src, fallenBackFor]);

  // WHY: ошибка до гидрации теряется — React её не повторяет; к этому моменту битая картинка уже complete с нулевой шириной
  useEffect(() => {
    const img = imgRef.current;
    if (img && img.complete && img.currentSrc && img.naturalWidth === 0) handleError();
  }, [handleError]);

  return (
    <img
      {...props}
      ref={imgRef}
      src={direct ? src : props.src}
      srcSet={direct ? steamImageSrcSet(src) : srcSet}
      sizes={props.sizes}
      fetchPriority={fetchPriority ?? props.fetchPriority}
      onError={handleError}
    />
  );
};
